// Matching a spreadsheet's idea of a player to a saved one. Independent of what is being
// imported - the scorecard import maps rows onto a fixture's squad, the squad import maps them
// onto the club's player pool - so both hand this the records to match against and get the same
// four answers back: matched, matched loosely, several candidates, or nothing.
//
// Names in an exported file are not reliable keys. Two players in a club really do share a name,
// case and punctuation drift, and middle names come and go, so a match is offered rather than
// assumed: every wizard using this must let the admin point a row at a different record.

export const normalize = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')

// Name tokens sorted, so "Md Kawser Islam" and "Kawser Islam Md" land on the same key. Used
// only after an exact match has failed, and only when the key belongs to one record.
export const looseKey = (name) =>
  String(name ?? '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .sort()
    .join('')

export const guessColumn = (headers, patterns) =>
  headers.find((h) => patterns.some((p) => normalize(h).includes(p))) || ''

// Takes a header out of the pool as it is claimed, so `Player ID` cannot also be read as the
// player name column and a stat column cannot be read twice.
export const takeColumn = (pool, patterns) => {
  const found = guessColumn(pool, patterns)
  if (found) pool.splice(pool.indexOf(found), 1)
  return found
}

// Claims whichever headers identify the player. `Player ID` is taken first so it cannot be
// mistaken for the name column, and First/Last Name wins over a single Player column when a
// file carries both.
export const takePlayerColumns = (pool) => {
  const playerIdCol = takeColumn(pool, ['playerid'])
  const firstNameCol = takeColumn(pool, ['firstname'])
  const lastNameCol = takeColumn(pool, ['lastname'])
  const nameCol =
    firstNameCol || lastNameCol
      ? ''
      : takeColumn(pool, ['playername', 'fullname', 'player', 'name'])

  return {
    playerIdCol,
    firstNameCol,
    lastNameCol,
    nameCol,
    found: Boolean(playerIdCol || firstNameCol || lastNameCol || nameCol),
  }
}

// Pulls the name and id out of one sheet row, joining First and Last Name when that is how the
// file spells a player.
export const readPlayerCells = (row, columns) => {
  const { playerIdCol, firstNameCol, lastNameCol, nameCol } = columns
  const csvName = nameCol
    ? String(row[nameCol] ?? '').trim()
    : [firstNameCol ? row[firstNameCol] : '', lastNameCol ? row[lastNameCol] : '']
        .map((part) => String(part ?? '').trim())
        .filter(Boolean)
        .join(' ')

  return {
    csv_name: csvName,
    csv_player_id: playerIdCol ? String(row[playerIdCol] ?? '').trim() : '',
  }
}

// Indexes the records a file can be matched against. `accessors` says how to read a record's
// own key (what a mapping stores), the player id a file would quote, and the player's name -
// which differ between a scorecard row and a player record.
export const buildPlayerIndex = (records, accessors) => {
  const { getKey, getPlayerId, getName } = accessors
  const byKey = new Map()
  const byPlayerId = new Map()
  const byName = new Map()
  const byLooseName = new Map()

  const push = (map, key, record) => {
    if (!key) return
    if (!map.has(key)) map.set(key, [])
    map.get(key).push(record)
  }

  ;(records || []).forEach((record) => {
    byKey.set(String(getKey(record)), record)

    const playerId = getPlayerId(record)
    if (playerId !== undefined && playerId !== null && String(playerId).trim() !== '') {
      // Ids get compared case-insensitively: these are UUIDs, and a spreadsheet round trip
      // does not always give them back in the case it was handed.
      byPlayerId.set(String(playerId).trim().toLowerCase(), record)
    }

    push(byName, normalize(getName(record)), record)
    push(byLooseName, looseKey(getName(record)), record)
  })

  return { byKey, byPlayerId, byName, byLooseName }
}

// Resolution order: a mapping the admin made by hand, then the player id the file quotes, then
// the name exactly, then the name with its parts in any order. Anything that lands on more than
// one record is reported as ambiguous rather than guessed at.
export const resolvePlayer = (base, index) => {
  if (base.manual_id) {
    const record = index.byKey.get(String(base.manual_id))
    if (record) return { record, match: 'manual' }
  }

  const csvPlayerId = String(base.csv_player_id ?? '')
    .trim()
    .toLowerCase()
  if (csvPlayerId && index.byPlayerId.has(csvPlayerId)) {
    return { record: index.byPlayerId.get(csvPlayerId), match: 'player_id' }
  }

  const exact = index.byName.get(normalize(base.csv_name)) || []
  if (exact.length === 1) return { record: exact[0], match: 'name' }
  if (exact.length > 1) return { record: null, match: null, ambiguous: exact }

  const loose = index.byLooseName.get(looseKey(base.csv_name)) || []
  if (loose.length === 1) return { record: loose[0], match: 'loose' }
  if (loose.length > 1) return { record: null, match: null, ambiguous: loose }

  return { record: null, match: null }
}

// How a row found its record. Shown next to the name so an admin can see at a glance which rows
// the file matched on its own and which ones they pointed somewhere by hand.
export const MATCH_META = {
  name: { label: 'Matched by name', color: 'success' },
  player_id: { label: 'Matched by player ID', color: 'success' },
  loose: { label: 'Matched on name order', color: 'info' },
  manual: { label: 'Mapped by you', color: 'primary' },
}
